import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Avatar,
    Typography,
    CircularProgress,
    IconButton,
    Divider,
} from "@mui/material";
import { X, Search, Plus } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { chatService } from "../../api/chat";
import { useAuth } from "../../contexts/AuthContext";

// Simple debounce hook
function useDebounce(value, delay = 400) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(timer);
    }, [value, delay]);
    return debounced;
}

const NewChatModal = ({ open, onClose, onChatReady }) => {
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const [search, setSearch] = useState("");
    const debouncedSearch = useDebounce(search, 400);

    const role = user?.role || "";

    const { data, isLoading, isError } = useQuery({
        queryKey: ["messageableUsers", role, debouncedSearch],
        queryFn: () => chatService.getMessageableUsers(role, debouncedSearch),
        enabled: open && !!role,
    });

    // API may return paginated { results: [] } or a plain array
    const users = data?.results || (Array.isArray(data) ? data : []);

    // For professionals, also do client-side filter on recruiter list
    const filtered =
        role === "recruiter"
            ? users // server already filters for recruiters
            : users.filter((u) => {
                const name =
                    `${u.first_name ?? u.user?.first_name ?? ""} ${u.last_name ?? u.user?.last_name ?? ""}`.toLowerCase();
                return !debouncedSearch || name.includes(debouncedSearch.toLowerCase());
            });

    const { mutate: startChat, isPending } = useMutation({
        mutationFn: (targetUserId) => chatService.initializeChatRoom(targetUserId),
        onSuccess: (chatRoom) => {
            queryClient.invalidateQueries({ queryKey: ["conversationList"] });

            const otherUser =
                chatRoom.recruiter?.id === user?.id
                    ? chatRoom.professional
                    : chatRoom.recruiter;

            onChatReady({
                full_name: `${otherUser.first_name ?? ""} ${otherUser.last_name ?? ""}`.trim(),
                profile_picture: otherUser.profile_picture,
                last_seen: chatRoom.updated_at,
                firebase_thread_id: chatRoom.firebase_thread_id,
                chat_id: chatRoom.id,
                ...otherUser,
            });
            onClose();
        },
        onError: () => {
            // already handled via error state if needed
        },
    });

    const handleSelect = (recipient) => {
        const targetId = recipient.user?.id ?? recipient.id;
        if (targetId) startChat(targetId);
    };

    // Reset search when modal closes
    useEffect(() => {
        if (!open) setSearch("");
    }, [open]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="xs"
            PaperProps={{
                sx: { borderRadius: 3, overflow: "hidden" },
            }}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    background: "linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    py: 1.5,
                    px: 2,
                }}
            >
                <div className="flex items-center gap-2">
                    <Plus size={20} />
                    <span className="font-semibold text-base">New Conversation</span>
                </div>
                <IconButton size="small" onClick={onClose} sx={{ color: "white" }}>
                    <X size={18} />
                </IconButton>
            </DialogTitle>

            {/* Search */}
            <div className="px-4 pt-3 pb-2">
                <div className="flex items-center gap-2 px-3 py-1.5 border border-gray-200 rounded-full bg-gray-50 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition-all">
                    <Search size={15} className="text-gray-400 shrink-0" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={
                            role === "recruiter"
                                ? "Search verified professionals..."
                                : "Search recruiters..."
                        }
                        className="flex-1 bg-transparent text-sm outline-none text-gray-700 placeholder-gray-400"
                    />
                    {isLoading && <CircularProgress size={14} className="shrink-0" />}
                </div>
            </div>

            <Divider />

            {/* Recipients List */}
            <DialogContent sx={{ p: 0, maxHeight: 400, overflowY: "auto" }}>
                {isError && (
                    <Typography
                        className="text-center p-6 text-red-500"
                        variant="body2"
                    >
                        Could not load contacts.
                    </Typography>
                )}
                {!isLoading && !isError && filtered.length === 0 && (
                    <div className="flex flex-col items-center py-10 gap-2 text-gray-400">
                        <Search size={32} className="opacity-40" />
                        <Typography variant="body2">No contacts found</Typography>
                    </div>
                )}
                {filtered.map((recipient) => {
                    const firstName =
                        recipient.first_name ?? recipient.user?.first_name ?? "";
                    const lastName =
                        recipient.last_name ?? recipient.user?.last_name ?? "";
                    const backendFullName = recipient.full_name ?? recipient.user?.full_name;

                    const personalName = backendFullName || `${firstName} ${lastName}`.trim();
                    const companyName = recipient.company_name ?? recipient.user?.company_name;

                    // If searching for recruiters, show Company Name as Title, Recruiter Name as Subtitle
                    // If searching for professionals, show Professional Name as Title, Specialization as Subtitle
                    const title = (role === "professional" && companyName) ? companyName : (personalName || "Unknown");
                    const subtitle = role === "recruiter"
                        ? (recipient.specialization ?? recipient.user?.specialization ?? "Professional")
                        : (role === "professional" && companyName ? (personalName || "Recruiter") : (companyName || "Recruiter"));

                    const picture =
                        recipient.profile_picture ?? recipient.user?.profile_picture;
                    const targetId = recipient.user?.id ?? recipient.id;

                    return (
                        <button
                            key={targetId}
                            disabled={isPending}
                            onClick={() => handleSelect(recipient)}
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50 active:bg-blue-100 transition-colors text-left disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            <Avatar src={picture} alt={title} sx={{ width: 44, height: 44 }} />
                            <div className="flex flex-col flex-1 min-w-0">
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: "text.primary" }}
                                    className="truncate"
                                >
                                    {title}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    className="text-gray-400 truncate"
                                >
                                    {subtitle}
                                </Typography>
                            </div>
                        </button>
                    );
                })}
            </DialogContent>
        </Dialog>
    );
};

export default NewChatModal;
